/* ────────────────────────────────────────────────
   Calcula métricas de cotización con:
   • Horas pico mensuales + días reales
   • Valor de energía solar generada anual
   • Ahorro anual = Valor solar – Pago actual
───────────────────────────────────────────────── */
export function quoteFromTable(
  rows,
  tipo = "bimestral",
  {
    horasLuz = 5.7, // se conserva para fallback
    F1 = 0.88,
    F2 = 0.98,
    F3 = 0.95,
    panelW = 585,
    precioW = 0.815,
    override = {},
  } = {}
) {
  /* ─ presets x periodicidad ─ */
  const PRESET =
    {
      mensual: { rows: 12, usdMxn: 19.54 },
      bimestral: { rows: 6, usdMxn: 19.5 },
    }[tipo] ?? PRESET.bimestral;

  /* ─ Horas pico solares por mes (Sonora ~ Hermosillo) ─ */
  const horasPico = {
    0: 5.3, // enero
    1: 5.9, // febrero
    2: 6.4, // marzo
    3: 6.8, // abril
    4: 7.1, // mayo
    5: 6.9, // junio
    6: 6.7, // julio
    7: 6.4, // agosto
    8: 6.1, // septiembre
    9: 5.7, // octubre
    10: 5.3, // noviembre
    11: 5.0, // diciembre
  };

  /* ─ helpers ─ */
  const num = (x) => +String(x ?? 0).replace(/[$,]/g, "") || 0;
  const sum = (a, k) => a.reduce((s, b) => s + (b[k] || 0), 0);
  const rnd = (n, d = 2) => Number(n.toFixed(d));

  /* ─ normaliza columnas ─ */
  const parsed = rows.map((r) => ({
    periodo: r.Periodo ?? r.periodo ?? "-",
    kwh: num(r.kWh ?? r.kwh),
    costo: num(r.Importe ?? r.costo),
    tarifa: r.Tarifa ?? r.tarifa ?? "—",
  }));

  /* ─ totales base ─ */
  const take = parsed.slice(0, PRESET.rows);
  const totAnualKwh = sum(take, "kwh");
  const totAnualCost = sum(take, "costo");
  const precioPromKwh = totAnualCost / (totAnualKwh || 1);

  /* ─ potencia requerida (cálculo bruto) ─ */
  const promDiaKwh = totAnualKwh / 365;
  const kwReqBruto = promDiaKwh / (horasLuz * F1 * F2 * F3);

  /* ─ nº paneles con regla .3 ─ */
  const exactPanels = (kwReqBruto * 1000) / panelW;
  const dec = exactPanels - Math.floor(exactPanels);
  const numSP585 =
    dec >= 0.3 ? Math.ceil(exactPanels) : Math.floor(exactPanels);

  /* ─ potencia final (redondeada) ─ */
  const kwFinal = (numSP585 * panelW) / 1000;

  /* ─ producción mensual / valor solar ─ */
  const monthly = take.map((r) => {
    /* fecha de inicio del periodo => mes 0-11 */
    const start = r.periodo.split("-")[0].trim(); // "2025-04-22"
    const month = new Date(start).getMonth(); // 0-11
    const year = new Date(start).getFullYear();
    const hp = horasPico[month] ?? horasLuz; // fallback
    const days = new Date(year, month + 1, 0).getDate(); // días del mes

    const prodKwh = kwFinal * hp * days; // producción mensual
    const $kWhMes = r.kwh ? r.costo / r.kwh : precioPromKwh;
    const valorMes = prodKwh * $kWhMes;

    return {
      ...r,
      mes: month,
      hp,
      days,
      prodKwh,
      precioKwh: $kWhMes,
      valorMes,
    };
  });

  const prodAnual = sum(monthly, "prodKwh");
  const valorEnergiaAnual = sum(monthly, "valorMes");

  /* ─ precios del sistema ─ */
  const priceUsdCalc = kwFinal * 1000 * precioW;
  const priceMxnCalc = priceUsdCalc * PRESET.usdMxn;

  /* ─ overrides — permiten forzar valores exactos (C18, D18, C26) ─ */
  const {
    kwReq = kwReqBruto,
    precioUsd = priceUsdCalc,
    precioMxn = priceMxnCalc,
    ahorroAnual = valorEnergiaAnual - totAnualCost,
    retornoAnios = priceMxnCalc / (valorEnergiaAnual || 1),
  } = override;

  return {
    historico: monthly, // incluye precioKwh y valorMes
    tarifa: parsed[0].tarifa,

    /* totales factura */
    totAnualKwh: rnd(totAnualKwh, 0),
    totAnualCost: rnd(totAnualCost),

    /* producción / valor solar */
    prodAnual: rnd(prodAnual, 0),
    valorEnergiaSolarAnual: rnd(valorEnergiaAnual),
    ahorroAnual: rnd(ahorroAnual),

    /* potencia y sistema */
    kwPotenciaReq: rnd(kwReq),
    numSP585,
    kwPotenciaFinal: rnd(kwFinal),

    /* precios sistema */
    precioSistemaUsd: rnd(precioUsd),
    precioSistemaMxn: rnd(precioMxn),
    retornoAnios: rnd(retornoAnios, 2),

    creditoMes12: rnd((precioMxn * 1.25) / 12),
  };
}
